'use client';

import { Progress, ProgressIndicator } from '@radix-ui/react-progress';
import { Children, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/app_lib/utils/utils';

function getKey(item: ReactNode, index: number) {
  if (typeof item === 'object' && item !== null && 'key' in item && item.key != null) {
    return String(item.key);
  }
  return `auto-carousel-${index}`;
}

export interface AutoCarouselProps {
  children?: ReactNode;
  className?: string;
  /** Auto-advance interval in ms. 0 is not allowed. Defaults to 6000. */
  autoAdvanceMs?: number;
}

export const AutoCarousel = ({ children, className, autoAdvanceMs: initialAutoAdvanceMs }: AutoCarouselProps) => {
  const autoAdvanceMs = useMemo(() => {
    if (initialAutoAdvanceMs == null || initialAutoAdvanceMs <= 0) {
      return 6000;
    }
    return initialAutoAdvanceMs;
  }, [initialAutoAdvanceMs]);
  const items = useMemo(() => Children.toArray(children), [children]);
  const count = items.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const stepNextRef = useRef<() => void>(() => {});

  const stepTo = useCallback(
    (index: number) => {
      setActiveIndex(() => {
        if (index < 0) {
          return count - 1;
        }
        if (index >= count) {
          return 0;
        }
        return index;
      });
    },
    [count],
  );

  const stepNext = useCallback(() => stepTo(activeIndex + 1), [stepTo, activeIndex]);

  stepNextRef.current = stepNext;

  // Restart the fill animation whenever the active slide changes (including auto-advance).
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeIndex intentionally restarts the rAF loop
  useEffect(() => {
    if (!autoAdvanceMs || count <= 1) {
      setProgressValue(0);
      return;
    }

    setProgressValue(0);
    const start = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= autoAdvanceMs) {
        setProgressValue(100);
        stepNextRef.current();
        return;
      }
      setProgressValue((elapsed / autoAdvanceMs) * 100);
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [activeIndex, autoAdvanceMs, count]);

  if (count === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col items-center layout-gap-md', className)}>
      <div className='relative overflow-hidden'>
        {items.map((item, index) => (
          <div
            aria-hidden={index !== activeIndex}
            className={cn(
              'w-full',
              index === activeIndex ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none',
            )}
            key={getKey(item, index)}
          >
            {item}
          </div>
        ))}
      </div>

      <div className={cn('flex gap-xs', count < 2 && 'hidden')}>
        {Array.from({ length: count }).map((_, index) => {
          let currProgressValue = progressValue;
          if (index > activeIndex) {
            currProgressValue = 0;
          }
          if (index < activeIndex) {
            currProgressValue = 100;
          }
          return (
            <div
              className='h-px w-10 bg-helper'
              key={`auto-carousel-progress-${getKey(items[index], index)}`}
            >
              <Progress
                aria-label='Carousel auto-advance progress'
                className='relative h-px w-full min-w-0 overflow-hidden rounded-full bg-helper'
                getValueLabel={(value, max) =>
                  `${Math.round((value / max) * 100)}% until next slide, slide ${activeIndex + 1} of ${count}`
                }
                max={100}
                value={currProgressValue}
              >
                <ProgressIndicator
                  className='h-full w-full bg-blue-1'
                  style={{ transform: `translateX(-${100 - currProgressValue}%)` }}
                />
              </Progress>
            </div>
          );
        })}
      </div>
    </div>
  );
};
